import { Storage } from '@ionic/storage-angular';
import { Injectable } from '@angular/core';
import { RemoteService } from './remote.service';
import { SyncResult } from './model/sync-result';
import { BehaviorSubject, Subject } from 'rxjs';
import { AppStorage } from './helper/app-storage';
import { Nachricht, TextdateiTyp, Textdatei, NachrichtAbsenderTyp } from './model/swagger-model';
import { SystemService } from './system.service';
import { AppConfig } from './helper/app.config';
import { Logger } from './helper/app-error-logger';
import { MyApp } from './helper/app';

import moment from 'moment';

import { Utils } from './helper/utils';
import { UiHelper } from './helper/ui-helper';
import { RCMForeground } from './plugins/foreground-plugin';
import { PushNotificationSchema } from './plugins/push-plugin';

declare var cordova: any;

const log = new Logger("NachrichtService");

@Injectable({
    providedIn: 'root'
})
export class NachrichtService {
    alleNachrichten: Nachricht[] = null;

    anzahlNeueNachrichten = new BehaviorSubject<number>(0);

    nachrichtenListeGeaendert = new Subject<void>();

    dringendeNachrichtenQueue: Nachricht[] = [];
    dringendeNachrichtWirdAngezeigt = false;

    syncErforderlich = true;
    syncCounter = 0;

    constructor(private storage: Storage,
        private remoteService: RemoteService,
        private systemService: SystemService) {
    }

    async init() {
        // WebSocket-Event
        this.systemService.nachrichtenAktualisieren.subscribe(() => this.syncNachrichtenMitServer('nachrichtenAktualisieren'));

        // Firebase push
        this.systemService.pushNotificationEmpfangen.subscribe(notification => {
            if (notification.data) {
                const typ = notification.data.typ;

                if (typ === "nachricht") {
                    this.verarbeitePushNachricht(notification);
                }
            }
        });

        MyApp.internetAvailable.subscribe(async (p) => {
            if (this.syncErforderlich && AppConfig.current.token) {
                const result = await this.syncNachrichtenMitServer('internetAvailable');

                if (result.success) {
                    this.syncErforderlich = false;
                }
            }
        });

        // Einmal je Stunde nach neuen Nachrichten pollen
        setInterval(async () => {
            if (MyApp.isInternetAvailable() && AppConfig.current.token) {
                const result = await this.syncNachrichtenMitServer('setInterval');

                if (!result.success) {
                    this.syncErforderlich = true;
                }
            } else {
                this.syncErforderlich = true;
            }
        }, 60 * 60 * 1000);

        setTimeout(async () => {
            if (AppConfig.current.token) {
                this.syncNachrichtenMitServer('setTimeout');
            }
        }, 15000);

        const alleNachrichten = await this.getAlleNachrichten();

        this.anzahlNeueNachrichten.next(alleNachrichten.filter(p => !p.Gelesen && p.AbsenderTyp !== NachrichtAbsenderTyp.Geraet).length);

        AppConfig.changed.subscribe(() => {
            this.aktualisiereNachrichtenIcon();
        });

        this.aktualisiereNachrichtenIcon();
    }

    async loescheDaten() {
        await AppStorage.current.set('nachrichten', []);

        this.alleNachrichten = null;
        this.anzahlNeueNachrichten.next(0);
        this.nachrichtenListeGeaendert.next();

        this.aktualisiereNachrichtenIcon();
    }

    aktualisiereNachrichtenIcon() {
        const appConfig = AppConfig.current;
        const app = MyApp.current;

        if (MyApp.current.nachrichtenVerfuegbar.getValue()) {
            switch (appConfig.einstellungen.NachrichtenSymbolInTitelleiste) {
                case 'ja':
                    Utils.next(app.NachrichtenSymbolInTitelleiste, true);
                    break;

                case 'nein':
                    Utils.next(app.NachrichtenSymbolInTitelleiste, false);
                    break;

                case 'vorhanden':
                    Utils.next(app.NachrichtenSymbolInTitelleiste, this.anzahlNeueNachrichten.getValue() > 0);
                    break;

            }
        } else {
            Utils.next(app.NachrichtenSymbolInTitelleiste, false);
        }
    }

    async sendeNachricht(text: string): Promise<void> {
        const fahrer = MyApp.current.fahrer.getValue();
        const fahrzeug = MyApp.current.fahrzeug.getValue();

        let absender = '';

        if (fahrer) {
            absender = fahrer.vorname + ' ' + fahrer.nachname;
        }

        if (fahrzeug) {
            absender += ' / ' + fahrzeug.kennzeichen;
        }

        const nachricht: Nachricht = {
            Timestamp: Date.now(),
            Guid: Utils.uuid(),
            AbsenderTyp: NachrichtAbsenderTyp.Geraet,
            Absender: absender,
            Datum: Utils.nowIsoDateString(),
            Gelesen: false,
            // Gesendet: true,
            // Status: 'neu',
            // GesendetDatum: moment(new Date()).format('YYYY-MM-DD HH:mm:ss'),
            Text: text,
            Dringend: false,
            PrevGuids: []
        };

        // Fülle PrevGuids
        const alleNachrichten = await this.getAlleNachrichten();

        if (alleNachrichten.length > 0) {
            // Ermittle die GUIDs der letzten 3 Nachrichten
            let startIdx = Math.max(0, alleNachrichten.length - 5);

            for (let i = startIdx; i < alleNachrichten.length; i++) {
                const n = alleNachrichten[i];

                if (n?.Guid) {
                    nachricht.PrevGuids.push(n.Guid);
                }
            }
        }

        await this.speichereNachricht(nachricht);

        const textdatei: Textdatei = {
            typ: TextdateiTyp.Nachricht,
            datum: Utils.nowIsoDateString(),
            geraeteNummer: AppConfig.current.geraeteNummer,
            nachricht
        };

        const mitGpsPosition = AppConfig.current.einstellungen.GpsPositionBeiNachricht === 'ja';

        await this.systemService.sendeTextdatei(textdatei, mitGpsPosition);

        this.nachrichtenListeGeaendert.next();

        // await this.syncNachrichtenMitServer();
    }

    async syncNachrichtenMitServer(info: string, loading: HTMLIonLoadingElement = null): Promise<SyncResult> {
        try {
            log?.debug('syncNachrichtenMitServer: ' + info);

            if (!AppConfig.current.token) {
                log?.debug('syncNachrichtenMitServer: abbruch, kein Token');

                return {
                    success: false
                };
            }

            // Lade alle lokalen Nachrichten
            const nachrichten = await this.getAlleNachrichten();

            let lastTimestamp = await AppStorage.current.get('nachrichten-last-timestamp', true);

            if (!lastTimestamp) {
                lastTimestamp = 0;
            }

            let neueNachrichten = await this.remoteService.getNeueNachrichten(lastTimestamp);

            if (!neueNachrichten) {
                return {
                    errorMessage: 'Nachrichten konnten nicht vom Server geladen werden. Bitte Internetverbindung kontrollieren.',
                    success: false
                };
            }

            if (neueNachrichten.length) {
                neueNachrichten = neueNachrichten.sort((a, b) => a.Timestamp - b.Timestamp);

                const dringendeNachrichten: Nachricht[] = [];
                let neueNachrichtGefunden = false;

                for (const nachricht of neueNachrichten) {
                    if (!nachrichten.find(p => p.Guid === nachricht.Guid)) {
                        nachrichten.push(nachricht);
                        neueNachrichtGefunden = true;

                        if (nachricht.Timestamp > lastTimestamp) {
                            lastTimestamp = nachricht.Timestamp;
                        }

                        if (nachricht.Dringend) {
                            dringendeNachrichten.push(nachricht);
                        }
                    }
                }

                if (neueNachrichtGefunden) {
                    this.speichereNachrichten(nachrichten);

                    await AppStorage.current.set('nachrichten-last-timestamp', lastTimestamp);

                    this.nachrichtenListeGeaendert.next();

                    if (MyApp.current.NachrichtenSoundAbspielen.getValue()) {
                        this.systemService.beepFuerNeueNachricht();
                    }

                    if (dringendeNachrichten.length) {
                        if (loading) {
                            await loading.dismiss();
                        }

                        this.anzeigenDringendeNachrichten(dringendeNachrichten);
                    }
                }
            }

            return {
                success: true,
                newItems: 0
            };
        } catch (err) {
            log.warn('syncNachrichtenMitServer: ' + Utils.getErrorMessage(err), err);

            return {
                errorMessage: err,
                success: false
            };
        }
    }

    anzeigenDringendeNachrichten(neueDringendeNachrichten: Nachricht[]) {
        for (const nachricht of neueDringendeNachrichten) {
            if (this.dringendeNachrichtenQueue.find(p => p.Guid == nachricht)) {
                log?.debug('Dringende Nachricht ist bereits in Queue vorhanden. Nicht anzeigen: ' + JSON.stringify(nachricht));
                continue;
            }

            this.dringendeNachrichtenQueue.push(nachricht);
        }

        try {
            this.dringendeNachrichtenQueue = this.dringendeNachrichtenQueue.sort((a, b) => a.Timestamp - b.Timestamp);
        } catch (err) {
            log.error(Utils.getErrorMessage(err))
        }

        this.anzeigenNaechsteDringendeNachricht();
    }

    async anzeigenNaechsteDringendeNachricht() {
        if (!this.dringendeNachrichtenQueue.length) {
            return;
        }

        if (this.dringendeNachrichtWirdAngezeigt) {
            log?.debug('Dringende Nachricht wird bereits angezeigt. Warte bis Alert-Fenster geschlossen wurde.');
            return;
        }

        this.dringendeNachrichtWirdAngezeigt = true;

        try {
            // Hole die erste Nachricht
            const nachricht = this.dringendeNachrichtenQueue[0];

            log?.debug('Anzeigen nächste dringende Nachricht: ' + nachricht?.Text);

            // Entferne den ersten Eintrag aus der Liste
            this.dringendeNachrichtenQueue.splice(0, 1);

            if (!nachricht.Gelesen) {
                if (!this.systemService.isVisible.getValue()) {
                    if (MyApp.isCordovaAvailable()) {
                        // LocalNotifications.schedule({
                        //     title: 'Neue Nachricht',
                        //     text: nachricht.Text,
                        //     vibrate: true,
                        //     wakeup: true,
                        //     badge: 0,
                        //     priority: 2,
                        //     lockscreen: true,
                        //     foreground: true,
                        //     launch: true,
                        //     channelName: 'Nachrichten',
                        //     sound: 'res://platform_default',
                        // });

                        // Anwendung in den Vordergrund bringen
                        this.systemService.moveToForeground('neue dringende Nachricht');
                    }
                }

                await UiHelper.showAlert(nachricht.Text, 'Neue Nachricht von ' + nachricht.Absender, true);

                const now = Utils.nowIsoDateString();

                const textdatei: Textdatei = {
                    typ: TextdateiTyp.NachrichtGelesen,
                    datum: now,
                    geraeteNummer: AppConfig.current.geraeteNummer,
                    Nachrichten: [{
                        Guid: nachricht.Guid,
                        Gelesen: true,
                        GelesenDatum: Utils.nowIsoDateString(),
                        GelesenVon: MyApp.current.getPersonalKey()
                    }]
                };

                await this.systemService.sendeTextdatei(textdatei, false);
            }
        } catch (err) {
            log.error(Utils.getErrorMessage(err));
        } finally {
            this.dringendeNachrichtWirdAngezeigt = false;
        }

        // Nächste Nachricht anzeigen
        this.anzeigenNaechsteDringendeNachricht();
    }

    async getAlleNachrichten(): Promise<Nachricht[]> {
        if (this.alleNachrichten) {
            this.sortNachrichten(this.alleNachrichten);

            return this.alleNachrichten;
        }

        let nachrichten: Nachricht[] = await AppStorage.current.get('nachrichten', false);

        if (!nachrichten) {
            nachrichten = [];
        }

        for (const nachricht of nachrichten) {
            this.fixNachricht(nachricht);
        }

        // Lösche zu alte Nachrichten
        const maxAlterTage = MyApp.current.NachrichtenMaxAlter.getValue();

        if (maxAlterTage > 0) {
            const zuAltDatum = moment().subtract(maxAlterTage, 'days');

            if (nachrichten.length > 0) {
                nachrichten = nachrichten.filter(p => {
                    if (!p.Datum) {
                        return false;
                    }

                    try {
                        const datum = moment(p.Datum);

                        if (datum > zuAltDatum) {
                            return true;
                        }
                    } catch (err) {
                        log.error(err);
                    }

                    return false;
                });
            }
        }

        this.sortNachrichten(nachrichten);

        this.alleNachrichten = [...nachrichten];

        return nachrichten;
    }

    async speichereNachricht(nachricht: Nachricht): Promise<void> {
        const nachrichten = await this.getAlleNachrichten();

        const index = nachricht.Guid
            ? nachrichten.findIndex(p => p.Guid === nachricht.Guid)
            : -1;

        if (index >= 0) {
            nachrichten[index] = nachricht;
        } else {
            nachrichten.push(nachricht);
        }

        await this.speichereNachrichten(nachrichten);
    }

    async speichereNachrichten(nachrichten): Promise<void> {
        log?.debug('speichereNachrichten');

        this.alleNachrichten = nachrichten;
        await AppStorage.current.set('nachrichten', nachrichten);

        const neueNachrichten = this.alleNachrichten.filter(p => !p.Gelesen && p.AbsenderTyp !== NachrichtAbsenderTyp.Geraet);
        this.anzahlNeueNachrichten.next(neueNachrichten.length);

        this.aktualisiereNachrichtenIcon();
    }

    private fixNachricht(nachricht: Nachricht) {
        // Timestamp in Millisekunden umwandeln in Unix-Timestamp (Sekunden)
        // Das ist notwendig für die Sortierung
        if (nachricht.Timestamp > 100000000000) {
            nachricht.Timestamp = Math.round(nachricht.Timestamp / 1000);
        }
    }

    // private async getMaxId() {
    //     let maxId = 0;

    //     const nachrichten = await this.getAlleNachrichten();

    //     if (nachrichten.length) {
    //         maxId = nachrichten.map(p => p.Id).reduce((prev, next) => next > prev ? next : prev);
    //     }

    //     return maxId;
    // }

    /**
     * Wird aufgerufen wenn eine neue Nachricht per Firebase-Push empfangen wurde
     */
    async verarbeitePushNachricht(notification: PushNotificationSchema) {
        log?.info('verarbeitePushNachricht', notification);

        const myCounter = ++this.syncCounter;

        let text = '';
        let nachricht: Nachricht = null;

        try {
            const nachrichten = await this.getAlleNachrichten();

            const docodedData = atob(notification.data.text);
            text = Utils.inflateToString(docodedData);

            nachricht = {
                Guid: notification.data.key,
                Dringend: notification.data.dringend == "1",
                Text: text,
                Absender: notification.data.absender,
                AbsenderTyp: NachrichtAbsenderTyp.Extern,
                Datum: notification.data.datum,
                Timestamp: notification.data.timestamp,
                Gelesen: false,
            }

            if (nachrichten.find(p => p.Guid === nachricht.Guid)) {
                log?.debug('Nachricht ist schon vorhanden. Ignorieren')
                return;
            }

            const prevGuids = notification.data.prevguids as string;

            if (prevGuids) {
                nachricht.PrevGuids = prevGuids.split(';');
            }

            nachrichten.push(nachricht);

            this.sortNachrichten(nachrichten);

            this.speichereNachrichten(nachrichten);

            MyApp.scheduleLocalNotification({
                id: 500,
                title: 'Neue Nachricht',
                body: text,
                // TODO CAPACITOR
                // vibrate: true,
                // wakeup: true,
                // badge: 1,
                // priority: 1,
                // lockscreen: true,
                // foreground: true,
                actionTypeId: 'yes-no'
            })

            // await AppStorage.current.set('nachrichten-last-timestamp', lastTimestamp);

            this.nachrichtenListeGeaendert.next();

            if (MyApp.current.NachrichtenSoundAbspielen.getValue()) {
                this.systemService.beepFuerNeueNachricht();
            }

            await RCMForeground.bringToFront();

        } catch (err) {
            log.error('verarbeitePushNachricht: ' + Utils.getErrorMessage(err));

            this.syncErforderlich = true;
        }

        if (this.syncErforderlich && this.systemService.isInternetAvailable()) {
            log?.debug('syncErforderlich und InternetAvailable')

            setTimeout(async () => {
                if (this.syncErforderlich && myCounter === this.syncCounter) {
                    const result = await this.syncNachrichtenMitServer('setTimeout 2');

                    if (result.success) {
                        this.syncErforderlich = false;
                    }
                }
            }, 2500);
        }

        if (nachricht && nachricht.Dringend) {
            this.anzeigenDringendeNachrichten([nachricht]);
        }
    }

    sortNachrichten(nachrichten: Nachricht[]) {
        // Korrigiere den Timestamp von allen Nachrichten.
        for (const nachricht of nachrichten) {
            this.fixNachricht(nachricht);
        }

        nachrichten.sort((a, b) => {
            if (a.PrevGuids?.length && a.PrevGuids.includes(b.Guid)) {
                return 1;
            }

            if (b.PrevGuids?.length && b.PrevGuids.includes(a.Guid)) {
                return -1;
            }

            if (a.Timestamp < b.Timestamp) {
                return -1;
            } else if (a.Timestamp > b.Timestamp) {
                return 1;
            }

            // Bis hierhin sollten wir eigentlich nie kommen.
            // Als Fallback wird aber noch das Datum im ISO-Format geprüft.
            // Hier kann es aber Probleme mit unterschiedlichen Formatierungen geben (Zulu und GMT+x)
            if (a.Datum < b.Datum) {
                return -1;
            } else if (a.Datum > b.Datum) {
                return 1;
            }

            return 0;
        });
    }
}
